import React, { useState, useCallback } from 'react';
import Login from './components/Login';
import OrgSelection from './components/OrgSelection';
import UsersList from './components/UsersList';
import Dashboards from './components/Dashboards';
import DashboardSummariser from './components/DashboardSummariser';
import OrgDashboardVisibility from './components/OrgDashboardVisibility';
import DashboardSkillSetupGuide from './components/DashboardSkillSetupGuide';
import DashboardSkillSetupExistingRepos from './components/DashboardSkillSetupExistingRepos';
import MunshotDataApiDocs from './components/MunshotDataApiDocs';
import CreateAgent from './components/CreateAgent';
import EditAgent from './components/EditAgent';
import UploadOrgIcon from './components/UploadOrgIcon';
import UserInvoices from './components/UserInvoices';
import BrokerDashOperations from './components/BrokerDashOperations';
import ModifyLibraryAgents from './components/ModifyLibraryAgents';
import AgentCategoryModify from './components/AgentCategoryModify';
import SheetsTemplates from './components/SheetsTemplates';
import WorkflowTemplates from './components/WorkflowTemplates';
import CredentialManager from './components/CredentialManager';
import GithubActions from './components/GithubActions';
import ClientFinder from './components/ClientFinder';
import Sidebar, { SIDEBAR_WIDTH } from './components/Sidebar';
import BackendLogsPanel from './components/BackendLogsPanel';
import LogsWelcomeTooltip from './components/LogsWelcomeTooltip';
import AdminHome, {
  ModuleView,
  AdminBreadcrumb,
  getModuleById,
  getActionLabel,
} from './components/AdminHome';

const LOGS_WELCOME_SEEN_KEY = "adminLogsWelcomeSeen";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [createdAgent, setCreatedAgent] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [activeComponent, setActiveComponent] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState(null);
  const [editAgentId, setEditAgentId] = useState(null);
  const [logsPanelOpen, setLogsPanelOpen] = useState(false);
  const [showLogsWelcome, setShowLogsWelcome] = useState(false);

  const handleLogsIconInteract = useCallback(() => {
    if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem(LOGS_WELCOME_SEEN_KEY)) {
      setShowLogsWelcome(true);
    }
  }, []);

  const dismissLogsWelcome = useCallback(() => {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(LOGS_WELCOME_SEEN_KEY, "1");
    setShowLogsWelcome(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleSelectModule = (moduleId) => {
    const mod = getModuleById(moduleId);
    setActiveModule(moduleId);
    setEditAgentId(null);
    setActiveComponent(mod?.directAction ?? null);
  };

  const handleSelectAction = (componentName, { anchor = null } = {}) => {
    setActiveComponent(componentName);
    setActiveAnchor(anchor);
    setEditAgentId(null);
  };

  const handleGoHome = () => {
    setActiveModule(null);
    setActiveComponent(null);
    setEditAgentId(null);
  };

  const handleGoToModule = () => {
    const mod = activeModule ? getModuleById(activeModule) : null;
    setEditAgentId(null);
    setActiveComponent(mod?.directAction ?? null);
  };

  const handleClose = () => {
    setActiveComponent(null);
    setEditAgentId(null);
  };

  const handleEditAgent = (agentId) => {
    setActiveModule('agents');
    setEditAgentId(agentId);
    setActiveComponent('edit');
  };

  const mainContentStyle = {
    marginLeft: SIDEBAR_WIDTH,
    width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
    maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
    minHeight: "100vh",
    backgroundColor: "#eef2ff",
    padding: "0 20px 40px",
    boxSizing: "border-box",
  };

  const pageShellStyle = {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  };

  const pageContentStyle = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
  };

  const componentWrapperStyle = {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "30px",
    boxShadow: "0 2px 8px rgba(55, 48, 163, 0.08)",
    border: "1px solid rgba(99, 102, 241, 0.1)",
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const currentModule = activeModule ? getModuleById(activeModule) : null;
  const actionLabel = activeComponent ? getActionLabel(activeComponent) : null;

  const renderActiveComponent = () => {
    switch (activeComponent) {
      case 'adduser':
        return <OrgSelection onClose={handleClose} />;
      case 'userslist':
        return <UsersList onClose={handleClose} />;
      case 'dashboards':
        return <Dashboards onClose={handleClose} />;
      case 'dashboardSummariser':
        return <DashboardSummariser onClose={handleClose} />;
      case 'orgDashboardVisibility':
        return <OrgDashboardVisibility onClose={handleClose} />;
      case 'dashboardSkillGuide':
        return <DashboardSkillSetupGuide onClose={handleClose} />;
      case 'dashboardSkillExistingGuide':
        return <DashboardSkillSetupExistingRepos onClose={handleClose} />;
      case 'munshotDataApiDocs':
        return <MunshotDataApiDocs onClose={handleClose} initialApiId={activeAnchor} />;
      case 'create':
        return <CreateAgent onAgentCreated={setCreatedAgent} onClose={handleClose} />;
      case 'edit':
        return <EditAgent agent={editAgentId ? { id: editAgentId } : createdAgent} onClose={handleClose} />;
      case 'modify':
        return <ModifyLibraryAgents onClose={handleClose} onEditAgent={handleEditAgent} />;
      case 'upload':
        return <UploadOrgIcon onClose={handleClose} />;
      case 'userInvoices':
        return <UserInvoices onClose={handleClose} />;
      case 'brokerDashOperations':
        return <BrokerDashOperations onClose={handleClose} />;
      case 'agentCategoryModify':
        return <AgentCategoryModify onClose={handleClose} />;
      case 'sheetsTemplates':
        return <SheetsTemplates onClose={handleClose} />;
      case 'workflowTemplates':
        return <WorkflowTemplates onClose={handleClose} />;
      case 'credentialManager':
        return <CredentialManager onClose={handleGoHome} />;
      case 'githubActions':
        return <GithubActions onClose={handleGoHome} />;
      case 'clientFinder':
        return <ClientFinder onClose={handleGoHome} />;
      default:
        return null;
    }
  };

  return (
    <>
      {!isAuthenticated ? (
        <Login onLogin={handleLogin} />
      ) : (
        <>
          <Sidebar
            onLogout={() => setIsAuthenticated(false)}
            onToggleLogs={() => setLogsPanelOpen((o) => !o)}
            onLogsIconInteract={handleLogsIconInteract}
          />
          <BackendLogsPanel open={logsPanelOpen} onClose={() => setLogsPanelOpen(false)} />
          <LogsWelcomeTooltip visible={showLogsWelcome} onDismiss={dismissLogsWelcome} />
          <div style={mainContentStyle}>
            <div style={pageShellStyle}>
              <div style={pageContentStyle}>
                {activeComponent && currentModule ? (
                  <>
                    <AdminBreadcrumb
                      module={currentModule}
                      actionLabel={actionLabel}
                      onHome={handleGoHome}
                      onModule={handleGoToModule}
                    />
                    <div style={componentWrapperStyle}>
                      {renderActiveComponent()}
                    </div>
                  </>
                ) : activeModule && currentModule ? (
                  <ModuleView
                    module={currentModule}
                    onBack={handleGoHome}
                    onSelectAction={handleSelectAction}
                  />
                ) : (
                  <AdminHome onSelectModule={handleSelectModule} />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default App;
